import { AccountListItem, ActionType } from "./accounts";
import {
  AccountPlanStatus,
  ActivitiesStatus,
  KeyRoles,
  RateIndicator,
  RoleStatus,
} from "./enums";
import { IndicatorValues } from "./apiTypes";

export interface Employee {
  id: string;
  name: string;
  position: string;
}

export type IntegrationData = {
  campaignId: string;
  actionType: ActionType;
  campaignImportFlow: string;
};

export type CompanyStatusData = {
  analysis_status?: AnalysisData;
  scraping_status?: AnalysisData;
  scraping_update_status?: AnalysisData;
  generation_status?: GenerationData;
  account_plan_status?: AccountPlanStatusData;
};

export type UserRoles = {
  isOwner: boolean;
  isAdmin: boolean;
  isEditor?: boolean;
  isOnlyViewer: boolean;
  userEmail: string;
};

export interface CompanyState {
  isFetchTableMembers: boolean;
  discardTableChanges: boolean;
  company: string;
  account_id: string;
  analysisStatus: string;
  loadingAnalyse: string;
  uploadModal: string;
  update: string;
  updateAnalytics: boolean;
  integrationData: IntegrationData | null;
  createProductAnalyses: boolean;
  status: CompanyStatusData;
  recalculation_status: string;
  mismatchData: MismatchData | null;
  importDataStatus: ImportDataStatus | null;
  isNotificationsOpen: boolean;
  updatedCompanies: { [key: string]: AccountListItem };
  userRoles: UserRoles;
  isModalOpen: boolean;
  removedID: string;
  updatedStatus: { [key: string]: AccountPlanStatus };
}

export interface ChartItem {
  first_name: string;
  last_name: string;
  job_title: string;
  committee_probability: number;
  cluster_probability: number;
}

export interface Company {
  name: string;
  employees_min: number;
  employees_max: number;
  industry: string;
  product_url?: string;
  size: string;
}

export interface SummaryData {
  committee_members: number;
  cluster_members: number;
  engagement_score: number;
}

export interface Summary {
  total: {
    enriched: SummaryData;
    original: SummaryData;
  };
  expected: {
    committee_members: number;
    cluster_members: number;
    committee_range: {
      min: number;
      max: number;
    };
    cluster_range: {
      min: number;
      max: number;
    };
  };
  percentage: {
    cluster: {
      original: number;
      original_indicator?: RateIndicator;
      original_changed_at?: string;
      original_change?: number;
      enriched: number;
      enriched_indicator?: RateIndicator;
      enriched_changed_at?: string;
      enriched_change?: number;
    };
    committee: {
      original: number;
      original_indicator?: RateIndicator;
      original_changed_at?: string;
      original_change?: number;
      enriched: number;
      enriched_indicator?: RateIndicator;
      enriched_changed_at?: string;
      enriched_change?: number;
    };
    engagement: {
      score: number;
      indicator?: RateIndicator;
      changed_at?: string;
      change?: number;
    };
  };
  title_gaps: string[];
  role_gaps: string[];
}

export interface CompanyInfoProps {
  name: string;
  size: string;
  contactsImported: string;
  industry: string;
}

export interface CampaignMember {
  first_name: string;
  last_name: string;
  job_title: string;
  id: string;
  role: string;
  committee_probability: number;
  cluster_probability: number;
  seniority: string;
  campaign_id: string;
  engagement_score: number;
  data_source: string;
  highlight_committee: boolean;
  highlight_cluster: boolean;
  committee_status: string;
  cluster_status: string;
  full_name: string;
  warning_tooltip?: string;
  linkedin_updated_at?: string;
  role_id?: KeyRoles;
  is_edited: boolean;
  committee_probability_indicator?: RateIndicator;
  cluster_probability_indicator?: RateIndicator;
  engagement_score_indicator?: RateIndicator;
  is_hidden: boolean;
  is_active?: boolean;
  status?: RoleStatus;
  cluster: IndicatorValues;
  committee: IndicatorValues;
  engagement: IndicatorValues;
  sdr_playbook_status?: ActivitiesStatus;
}

export type ToastMessage = {
  campaign_id: string;
  title: string;
  description: string;
  type: "success" | "error";
};

export enum SSEEvents {
  "analysis_status" = "analysis_status",
  "scraping_status" = "scraping_status",
  "scraping_update_status" = "scraping_update_status",
  "generation_status" = "generation_status",
  "recalculation_status" = "recalculation_status",
  "mismatch_update" = "mismatch_update",
  "import_data_status" = "import_data_status",
  "account_plan_status" = "account_plan_status",
  "campaign_user_stats" = "campaign_user_stats",
  "sdr_playbook_status" = "sdr_playbook_status",
  "toast_message" = "toast_message",
}

export interface AccountPlanStatusData {
  account_id: string;
  campaign_id?: string;
  member_id: string;
  status: AccountPlanStatus;
  message?: string;
  job_id?: string;
}

export enum AnalysisStatus {
  "ready" = "ready",
  "created" = "created",
  "queued" = "queued",
  "in_progress" = "in_progress",
  "completed" = "completed",
  "failed" = "failed",
  "updated" = "updated",
  "completed_no_data" = "completed_no_data",
}

export type RecalculationData = {
  status: AnalysisStatus;
  message: string;
  campaign_id: string;
};

export type MismatchData = {
  status: AnalysisStatus;
  campaign_id: string;
  mismatches: {
    total: number;
    is_syncing: boolean;
  };
};

export interface AnalysisData {
  status: AnalysisStatus;
  progress: number;
  message: string;
  account_id: string;
  campaign_id: string;
  name: string;
  date: string;
  item: AccountListItem | null;
}

export interface GenerationData {
  status: AnalysisStatus;
  progress: number;
  task_id: string;
  message: string;
}

export type ImportDataStatus = {
  status: AnalysisStatus;
  campaign_id: string;
  progress: number;
  message: string;
};

export type SSEDataTypes =
  | SSEData<SSEEvents.scraping_status, AnalysisData>
  | SSEData<SSEEvents.scraping_update_status, AnalysisData>
  | SSEData<SSEEvents.analysis_status, AnalysisData>
  | SSEData<SSEEvents.generation_status, GenerationData>;

export interface SSEData<K extends SSEEvents, T> {
  event: K;
  data: T;
}

export type MembersUpdateValues = {
  member_id: string;
  committee?: boolean;
  cluster?: boolean;
  role?: KeyRoles;
  custom_index?: number;
  original_index?: number;
};
